"use client";

import * as React from "react";
import type { CSSProperties } from "react";
import { ArrowUpRight, BookOpen } from "lucide-react";
import {
  captureMarketingEvent,
  getMarketingDistinctId,
} from "@/lib/marketing-analytics-client";
import { routes } from "@/lib/site-config";

const baseHref = routes.manualLanding.path;

type ManualFloatingCtaProps = {
  delay?: number;
};

export function ManualFloatingCta({
  delay = 0.96,
}: ManualFloatingCtaProps) {
  const [distinctId, setDistinctId] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    void getMarketingDistinctId().then((marketingDistinctId) => {
      if (!cancelled) {
        setDistinctId(marketingDistinctId);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <div
        style={{
          bottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))",
        }}
        className="fixed inset-x-4 z-50 flex justify-center pointer-events-none md:hidden"
      >
        <div
          style={
            {
              "--manual-cta-delay": `${Math.max(0, delay)}s`,
            } as CSSProperties
          }
          className="manual-cta-enter motion-reduce:animate-none"
        >
          <a
            href={baseHref}
            aria-label="Create a manual for yourself"
            onClick={() => {
              captureMarketingEvent("manual_cta_clicked", {
                app_area: "manual",
                cta_id: "floating_manual_mobile",
                route_name: "home.manual_floating_cta.mobile",
                has_marketing_distinct_id: !!distinctId,
              });
            }}
            className="pointer-events-auto inline-flex items-center gap-2.5 rounded-full border border-border/90 bg-background/96 px-3.5 py-3 shadow-[0_24px_60px_-28px_rgba(15,23,42,0.35),0_10px_24px_rgba(15,23,42,0.12)] ring-1 ring-foreground/[0.05] backdrop-blur-xl focus:outline-none"
          >
            <span className="whitespace-nowrap text-[14px] font-semibold tracking-[-0.02em] text-foreground">
              Create a manual for yourself
            </span>

            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-foreground text-background shadow-[0_10px_24px_-12px_rgba(15,23,42,0.55)]">
              <BookOpen className="size-[16px]" />
            </span>
          </a>
        </div>
      </div>

      <div className="fixed bottom-5 left-1/2 z-30 hidden h-[198px] w-[472px] -translate-x-1/2 pointer-events-none md:block">
        <div
          style={
            {
              "--manual-cta-delay": `${Math.max(0, delay)}s`,
            } as CSSProperties
          }
          className="manual-cta-enter relative h-full w-full motion-reduce:animate-none"
        >
          <a
            href={baseHref}
            aria-label="Create a manual for yourself"
            onClick={() => {
              captureMarketingEvent("manual_cta_clicked", {
                app_area: "manual",
                cta_id: "floating_manual",
                route_name: "home.manual_floating_cta",
                has_marketing_distinct_id: !!distinctId,
              });
            }}
            className="group pointer-events-auto absolute bottom-0 left-1/2 block -translate-x-1/2 focus:outline-none"
          >
            <div
              className="relative h-[78px] w-[360px] origin-bottom overflow-hidden rounded-[39px] border border-border/95 bg-background shadow-[0_28px_84px_-34px_rgba(15,23,42,0.32),0_14px_30px_rgba(15,23,42,0.12)] ring-1 ring-foreground/[0.05] backdrop-blur-xl transition-[width,height,border-radius,transform,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:h-[186px] group-hover:w-[444px] group-hover:-translate-y-1 group-hover:scale-[1.015] group-hover:rounded-[36px] group-hover:shadow-[0_36px_96px_-34px_rgba(15,23,42,0.42),0_18px_42px_rgba(15,23,42,0.12)] group-focus-visible:h-[186px] group-focus-visible:w-[444px] group-focus-visible:-translate-y-1 group-focus-visible:scale-[1.015] group-focus-visible:rounded-[36px] group-focus-visible:shadow-[0_36px_96px_-34px_rgba(15,23,42,0.42),0_18px_42px_rgba(15,23,42,0.12)] motion-reduce:transition-none"
            >
              <span className="absolute left-4 top-3.5 flex size-12 items-center justify-center rounded-full bg-foreground text-background shadow-[0_10px_24px_-12px_rgba(15,23,42,0.55)] transition-[top,left,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:left-[18px] group-hover:top-[18px] group-focus-visible:left-[18px] group-focus-visible:top-[18px] motion-reduce:transition-none">
                <BookOpen className="size-[18px]" />
              </span>

              <span className="absolute right-4 top-3.5 flex size-12 items-center justify-center rounded-full border border-border/80 bg-background text-foreground shadow-sm transition-[top,right,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:right-[18px] group-hover:top-[18px] group-hover:translate-y-[102px] group-focus-visible:right-[18px] group-focus-visible:top-[18px] group-focus-visible:translate-y-[102px] motion-reduce:transition-none">
                <ArrowUpRight className="size-4" />
              </span>

              <div className="absolute inset-0">
                <p className="absolute left-[76px] right-[76px] top-[14px] flex h-12 items-center justify-center whitespace-nowrap text-center text-[15px] font-semibold tracking-[-0.02em] text-foreground transition-[opacity,transform,filter] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-2 group-hover:opacity-0 group-hover:[filter:blur(6px)] group-focus-visible:-translate-y-2 group-focus-visible:opacity-0 group-focus-visible:[filter:blur(6px)] motion-reduce:transition-none">
                  Create a manual for yourself
                </p>

                <div className="absolute top-[18px] right-[76px] bottom-[18px] left-[92px] flex translate-y-2 flex-col gap-1.5 opacity-0 [filter:blur(8px)] transition-[opacity,transform,filter] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0 group-hover:opacity-100 group-hover:[filter:blur(0px)] group-focus-visible:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:[filter:blur(0px)] motion-reduce:transition-none">
                  <p className="flex h-12 items-center text-[15px] font-semibold tracking-[-0.02em] text-foreground">
                    Create a manual for yourself
                  </p>
                  <p className="text-sm leading-6 text-muted-foreground">
                    Get a shareable &quot;How to Work With Me&quot; manual. Understand
                    your own working needs, then use the relevant parts with a
                    manager or teammate.
                  </p>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </>
  );
}
